/**
 * MCP Appointments API Mutations
 * React Query mutation hooks for managing resources and appointments
 *
 * Gateway mapping: /mcp-appointments/* → mcp-appointments service
 */

import {
  useMutation,
  useQueryClient,
  type QueryClient
} from '@tanstack/react-query';
import { useRoleMutationGate } from '@/api/_shared/roleGate';
import { buildAdminMutationBody, withAdminClientParam, withAdminScopeParams } from '@/utils/apiRole';
import { canUseAdminTenantScope, isAdminEquivalentRole } from '@/utils/authz';
import { apiDelete, apiPatch, apiPost, apiPut } from '@/api/_shared/http';
import {
  roleResourcesBase,
  roleAppointmentsBase,
  roleServicesBase,
  roleServiceRolesBase,
  roleRescheduleRequestsBase
} from './paths';
import type {
  CreateResourceSimpleRequest,
  CreateResourceAdvancedRequest,
  CreateClientResourceSimpleRequest,
  CreateClientResourceAdvancedRequest,
  UpdateResourceRequest,
  CreateResourceResponse,
  UpdateResourceResponse,
  ResourcePolicyRequest,
  ClientResourcePolicyRequest,
  ResourcePolicyResponse,
  ResourceAvailabilityRequest,
  SetResourceAvailabilityResponse,
  ResourceExceptionRequest,
  AddResourceExceptionsResponse,
  RemoveResourceExceptionsResponse,
  CreateAppointmentRequest,
  CreateClientAppointmentRequest,
  CreateAppointmentResponse,
  UpdateAppointmentRequest,
  UpdateAppointmentResponse,
  UpdateAppointmentStatusRequest,
  UpdateAppointmentStatusResponse,
  ConfigureCalendarIcsPullRequest,
  ConfigureCalendarIcsPullResponse,
  EnsureCalendarExportFeedRequest,
  CalendarExportFeed,
  CreateServiceRequest,
  CreateClientServiceRequest,
  UpdateServiceRequest,
  ListServicesResponse,
  CreateServiceResponse,
  UpdateServiceResponse,
  SetServiceResourcesRequest,
  SetServiceResourcesResponse,
  CreateServiceAppointmentRequest,
  CreateClientServiceAppointmentRequest,
  CreateServiceAppointmentResponse,
  CreateServiceRoleRequest,
  CreateClientServiceRoleRequest,
  UpdateServiceRoleRequest,
  CreateServiceRoleResponse,
  UpdateServiceRoleResponse,
  SetResourceServiceRolesRequest,
  CreateCustomerRescheduleRequestBody,
  CreateCustomerCancellationRequestBody,
  AppointmentRescheduleRequest,
  AppointmentCancellationRequest,
  ApproveRescheduleRequestResponse,
  ResolveChangeRequestBody,
  CalDavAuthMetadata,
  CalDavDiscovery,
  CalDavFeed,
  CreateCalDavFeedRequest,
  CreateCalDavFeedResponse,
  UpdateCalDavFeedRequest
} from './interfaces';
import { calendarExportPublicBaseUrl } from '@/utils/calendarIcs';
import { invalidateAppointmentQueries } from '@/api/mcp-appointments/invalidate';
import {
  appointmentKeys,
  resourceKeys,
  serviceKeys,
  serviceRoleKeys
} from './keys';

function replaceServiceInListCaches(
  queryClient: QueryClient,
  updatedService: UpdateServiceResponse
): void {
  queryClient.setQueriesData<ListServicesResponse>(
    { queryKey: serviceKeys.lists() },
    (services) =>
      services?.map((service) =>
        service.id === updatedService.id ? updatedService : service
      )
  );
}

//
// Resource Mutations
// -------------------------------------------------

/**
 * Create a resource (role-aware), in either the simple (single-name) or the
 * advanced (multi-translation) body format.
 * - ADMIN: POST /api/resources with clientId in body
 * - CLIENT: POST /api/client/resources
 */
export const useCreateResourceMutation = () => {
  const queryClient = useQueryClient();
  const {
    role,
    assertReady,
    invalidateWithRoleKey,
    effectiveClientId,
    effectiveCompanyId
  } = useRoleMutationGate();

  return useMutation({
    mutationFn: async (
      body:
        | CreateClientResourceSimpleRequest
        | CreateResourceSimpleRequest
        | CreateClientResourceAdvancedRequest
        | CreateResourceAdvancedRequest
    ): Promise<CreateResourceResponse> => {
      assertReady();
      const bodyToSend = buildAdminMutationBody(role, body, effectiveClientId, effectiveCompanyId);
      return apiPost<CreateResourceResponse>(roleResourcesBase(role), bodyToSend);
    },
    onSuccess: () => {
      invalidateWithRoleKey(queryClient, resourceKeys.lists());
    },
    onError: (error) => {
      console.error('Failed to create resource:', error);
    }
  });
};

/**
 * Update a resource by ID (role-aware).
 * Updates role-scoped caches on success.
 *
 * @param resourceId - The resource to update
 * @param body - Fields to patch
 */
export const useUpdateResourceMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady, invalidateWithRoleKey, effectiveClientId } =
    useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      resourceId,
      body
    }: {
      resourceId: string;
      body: UpdateResourceRequest;
    }): Promise<UpdateResourceResponse> => {
      assertReady();
      const endpoint = `${roleResourcesBase(role)}/${resourceId}`;
      const params = withAdminClientParam(role, undefined, effectiveClientId || undefined);
      return apiPut<UpdateResourceResponse>(endpoint, body, { params });
    },
    onSuccess: (data, variables) => {
      invalidateWithRoleKey(queryClient, resourceKeys.lists());
      queryClient.setQueryData(resourceKeys.detail(roleKey, variables.resourceId), data);
    },
    onError: (error) => {
      console.error('Failed to update resource:', error);
    }
  });
};

/**
 * Delete a resource by ID (role-aware).
 * Removes all associated caches (policy, availability, exceptions) on success.
 *
 * @param resourceId - The resource to delete
 */
export const useDeleteResourceMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, queryKey, assertReady, invalidateWithRoleKey, effectiveClientId } =
    useRoleMutationGate();

  return useMutation({
    mutationFn: async (resourceId: string): Promise<void> => {
      assertReady();
      const endpoint = `${roleResourcesBase(role)}/${resourceId}`;
      const params = withAdminClientParam(role, undefined, effectiveClientId || undefined);
      await apiDelete<void>(endpoint, { params });
    },
    onSuccess: (_, resourceId) => {
      void queryClient.invalidateQueries({ queryKey: [...resourceKeys.lists(), ...queryKey] });
      queryClient.removeQueries({ queryKey: resourceKeys.detail(roleKey, resourceId) });
      queryClient.removeQueries({ queryKey: resourceKeys.policy(roleKey, resourceId) });
      queryClient.removeQueries({ queryKey: resourceKeys.availabilityAll(roleKey, resourceId) });
      queryClient.removeQueries({ queryKey: resourceKeys.exceptionsAll(roleKey, resourceId) });
    },
    onError: (error) => {
      console.error('Failed to delete resource:', error);
    }
  });
};

/**
 * Upsert the booking policy for a resource (role-aware).
 * Creates the policy if it doesn't exist, or replaces it if it does.
 *
 * @param resourceId - The resource whose policy to upsert
 * @param body - Policy fields
 */
export const useUpsertResourcePolicyMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady, effectiveClientId } = useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      resourceId,
      body
    }: {
      resourceId: string;
      body: ClientResourcePolicyRequest | ResourcePolicyRequest;
    }): Promise<ResourcePolicyResponse> => {
      assertReady();
      const endpoint = `${roleResourcesBase(role)}/${resourceId}/policy`;
      type PolicyBody = (ClientResourcePolicyRequest | ResourcePolicyRequest) & { clientId?: string };
      const bodyToSend: PolicyBody = canUseAdminTenantScope(role)
        ? { ...body, clientId: effectiveClientId as string }
        : body;
      return apiPut<ResourcePolicyResponse>(endpoint, bodyToSend);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(resourceKeys.policy(roleKey, variables.resourceId), data);
      queryClient.invalidateQueries({ queryKey: resourceKeys.detail(roleKey, variables.resourceId) });
    },
    onError: (error) => {
      console.error('Failed to upsert resource policy:', error);
    }
  });
};

/**
 * Replace all weekly availability rules for a resource (role-aware).
 *
 * @param resourceId - The resource whose availability to set
 * @param body - The new set of availability rules
 */
export const useSetResourceAvailabilityMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady, effectiveClientId } = useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      resourceId,
      body
    }: {
      resourceId: string;
      body: ResourceAvailabilityRequest;
    }): Promise<SetResourceAvailabilityResponse> => {
      assertReady();
      const endpoint = `${roleResourcesBase(role)}/${resourceId}/availability`;
      const params = withAdminClientParam(role, undefined, effectiveClientId || undefined);
      return apiPut<SetResourceAvailabilityResponse>(endpoint, body, { params });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: resourceKeys.availabilityAll(roleKey, variables.resourceId) });
      queryClient.invalidateQueries({ queryKey: resourceKeys.detail(roleKey, variables.resourceId) });
    },
    onError: (error) => {
      console.error('Failed to set resource availability:', error);
    }
  });
};

/**
 * Add one or more exceptions to a resource (role-aware).
 *
 * @param resourceId - The resource to add exceptions to
 * @param body - Exception items to add
 */
export const useAddResourceExceptionsMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady, effectiveClientId } = useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      resourceId,
      body
    }: {
      resourceId: string;
      body: ResourceExceptionRequest;
    }): Promise<AddResourceExceptionsResponse> => {
      assertReady();
      const endpoint = `${roleResourcesBase(role)}/${resourceId}/exceptions`;
      const params = withAdminClientParam(role, undefined, effectiveClientId || undefined);
      return apiPost<AddResourceExceptionsResponse>(endpoint, body, { params });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: resourceKeys.exceptionsAll(roleKey, variables.resourceId) });
      queryClient.invalidateQueries({ queryKey: resourceKeys.detail(roleKey, variables.resourceId) });
    },
    onError: (error) => {
      console.error('Failed to add resource exceptions:', error);
    }
  });
};

/**
 * Remove exceptions from a resource within a date range (role-aware).
 *
 * @param resourceId - The resource whose exceptions to remove
 * @param start - ISO-8601 range start
 * @param end - ISO-8601 range end
 */
export const useRemoveResourceExceptionsMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady, effectiveClientId } = useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      resourceId,
      start,
      end
    }: {
      resourceId: string;
      start: string;
      end: string;
    }): Promise<RemoveResourceExceptionsResponse> => {
      assertReady();
      const endpoint = `${roleResourcesBase(role)}/${resourceId}/exceptions`;
      const baseParams = withAdminClientParam<{ clientId?: string } | undefined>(
        role,
        undefined,
        effectiveClientId || undefined
      );
      return apiDelete<RemoveResourceExceptionsResponse>(endpoint, {
        params: { ...baseParams, start, end }
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: resourceKeys.exceptionsAll(roleKey, variables.resourceId) });
      queryClient.invalidateQueries({ queryKey: resourceKeys.detail(roleKey, variables.resourceId) });
    },
    onError: (error) => {
      console.error('Failed to remove resource exceptions:', error);
    }
  });
};

/**
 * Configure and optionally run an ICS pull sync for a resource (role-aware).
 */
export const useConfigureCalendarIcsPullMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady, selectedClientId, selectedCompanyId } = useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      resourceId,
      body
    }: {
      resourceId: string;
      body: ConfigureCalendarIcsPullRequest;
    }): Promise<ConfigureCalendarIcsPullResponse> => {
      assertReady();
      const endpoint = `${roleResourcesBase(role)}/${resourceId}/calendar/pull-ics-events`;
      return apiPost<ConfigureCalendarIcsPullResponse>(endpoint, body);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: resourceKeys.calendarImportFeeds(roleKey, variables.resourceId)
      });
      invalidateAppointmentQueries(queryClient, role, selectedClientId, selectedCompanyId);
    },
    onError: (error) => {
      console.error('Failed to configure calendar ICS pull:', error);
    }
  });
};

/**
 * Delete an ICS import feed and its imported blocker events (role-aware).
 */
export const useDeleteCalendarImportFeedMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady, selectedClientId, selectedCompanyId } = useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      resourceId,
      feedId
    }: {
      resourceId: string;
      feedId: string;
    }): Promise<void> => {
      assertReady();
      const endpoint = `${roleResourcesBase(role)}/${resourceId}/calendar/import-ics-feeds/${feedId}`;
      await apiDelete<void>(endpoint);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: resourceKeys.calendarImportFeeds(roleKey, variables.resourceId)
      });
      invalidateAppointmentQueries(queryClient, role, selectedClientId, selectedCompanyId);
    },
    onError: (error) => {
      console.error('Failed to delete calendar import feed:', error);
    }
  });
};

/**
 * Create or return the outbound ICS export feed for a resource (role-aware).
 */
export const useEnsureCalendarExportFeedMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady } = useRoleMutationGate();
  const baseUrl = calendarExportPublicBaseUrl();

  return useMutation({
    mutationFn: async ({
      resourceId,
      body
    }: {
      resourceId: string;
      body?: EnsureCalendarExportFeedRequest;
    }): Promise<CalendarExportFeed> => {
      assertReady();
      const endpoint = `${roleResourcesBase(role)}/${resourceId}/calendar/export-ics-feed`;
      return apiPost<CalendarExportFeed>(endpoint, body ?? {}, { params: { baseUrl } });
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        resourceKeys.calendarExportFeed(roleKey, variables.resourceId, baseUrl),
        data
      );
    },
    onError: (error) => {
      console.error('Failed to ensure calendar export feed:', error);
    }
  });
};

export const useDiscoverCalDavMutation = () => {
  const { role, assertReady } = useRoleMutationGate();
  return useMutation({
    mutationFn: ({ resourceId, serverUrl, authMetadata }: { resourceId: string; serverUrl: string; authMetadata: CalDavAuthMetadata }) => {
      assertReady();
      return apiPost<CalDavDiscovery>(`${roleResourcesBase(role)}/${resourceId}/calendar/caldav/discover`, { serverUrl, authMetadata });
    }
  });
};

export const useCreateCalDavFeedMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady } = useRoleMutationGate();
  return useMutation({
    mutationFn: ({ resourceId, body }: { resourceId: string; body: CreateCalDavFeedRequest }) => {
      assertReady();
      return apiPost<CreateCalDavFeedResponse>(`${roleResourcesBase(role)}/${resourceId}/calendar/caldav/feed`, body);
    },
    onSuccess: (data, variables) => queryClient.setQueryData(resourceKeys.calDavFeed(roleKey, variables.resourceId), data)
  });
};

export const useUpdateCalDavFeedMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady } = useRoleMutationGate();
  return useMutation({
    mutationFn: ({ resourceId, body }: { resourceId: string; body: UpdateCalDavFeedRequest }) => {
      assertReady();
      return apiPatch<CalDavFeed>(`${roleResourcesBase(role)}/${resourceId}/calendar/caldav/feed`, body);
    },
    onSuccess: (data, variables) => queryClient.setQueryData(resourceKeys.calDavFeed(roleKey, variables.resourceId), data)
  });
};

export const useDeleteCalDavFeedMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady } = useRoleMutationGate();
  return useMutation({
    mutationFn: async (resourceId: string) => {
      assertReady();
      await apiDelete(`${roleResourcesBase(role)}/${resourceId}/calendar/caldav/feed`);
    },
    onSuccess: (_, resourceId) => {
      queryClient.setQueryData(resourceKeys.calDavFeed(roleKey, resourceId), null);
    }
  });
};

export const useSyncCalDavFeedMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady } = useRoleMutationGate();
  return useMutation({
    mutationFn: (resourceId: string) => {
      assertReady();
      return apiPost<{ queued: boolean }>(`${roleResourcesBase(role)}/${resourceId}/calendar/caldav/feed/sync`, {});
    },
    onSuccess: (_, resourceId) => queryClient.invalidateQueries({ queryKey: resourceKeys.calDavFeed(roleKey, resourceId) })
  });
};


//
// Service Mutations
// -------------------------------------------------

/**
 * Create a service (role-aware).
 * - ADMIN: POST /api/services with clientId in body
 * - CLIENT: POST /api/client/services
 */
export const useCreateServiceMutation = () => {
  const queryClient = useQueryClient();
  const {
    role,
    assertReady,
    invalidateWithRoleKey,
    effectiveClientId,
    effectiveCompanyId
  } = useRoleMutationGate();

  return useMutation({
    mutationFn: async (
      body: CreateClientServiceRequest | CreateServiceRequest
    ): Promise<CreateServiceResponse> => {
      assertReady();
      const bodyToSend = buildAdminMutationBody(role, body, effectiveClientId, effectiveCompanyId);
      return apiPost<CreateServiceResponse>(roleServicesBase(role), bodyToSend);
    },
    onSuccess: () => {
      invalidateWithRoleKey(queryClient, serviceKeys.lists());
    },
    onError: (error) => {
      console.error('Failed to create service:', error);
    }
  });
};

/**
 * Update a service by ID (role-aware).
 *
 * @param serviceId - The service to update
 * @param body - Fields to patch
 */
export const useUpdateServiceMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady, invalidateWithRoleKey } = useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      serviceId,
      body
    }: {
      serviceId: string;
      body: UpdateServiceRequest;
    }): Promise<UpdateServiceResponse> => {
      assertReady();
      const endpoint = `${roleServicesBase(role)}/${serviceId}`;
      return apiPut<UpdateServiceResponse>(endpoint, body);
    },
    onSuccess: (data, variables) => {
      replaceServiceInListCaches(queryClient, data);
      invalidateWithRoleKey(queryClient, serviceKeys.lists());
      queryClient.setQueryData(serviceKeys.detail(roleKey, variables.serviceId), data);
    },
    onError: (error) => {
      console.error('Failed to update service:', error);
    }
  });
};

/**
 * Delete a service by ID (role-aware).
 *
 * @param serviceId - The service to delete
 */
export const useDeleteServiceMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady, invalidateWithRoleKey } = useRoleMutationGate();

  return useMutation({
    mutationFn: async (serviceId: string): Promise<void> => {
      assertReady();
      const endpoint = `${roleServicesBase(role)}/${serviceId}`;
      await apiDelete<void>(endpoint);
    },
    onSuccess: (_, serviceId) => {
      invalidateWithRoleKey(queryClient, serviceKeys.lists());
      queryClient.removeQueries({ queryKey: serviceKeys.detail(roleKey, serviceId) });
    },
    onError: (error) => {
      console.error('Failed to delete service:', error);
    }
  });
};

/**
 * Replace linked resources for a service (role-aware).
 * Replaces the full linked resource list; does not append.
 *
 * @param serviceId - The service whose resources to replace
 * @param body - New resource links
 */
export const useSetServiceResourcesMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady, invalidateWithRoleKey } = useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      serviceId,
      body
    }: {
      serviceId: string;
      body: SetServiceResourcesRequest;
    }): Promise<SetServiceResourcesResponse> => {
      assertReady();
      const endpoint = `${roleServicesBase(role)}/${serviceId}/resources`;
      return apiPut<SetServiceResourcesResponse>(endpoint, body);
    },
    onSuccess: (data, variables) => {
      replaceServiceInListCaches(queryClient, data);
      invalidateWithRoleKey(queryClient, serviceKeys.lists());
      queryClient.setQueryData(serviceKeys.detail(roleKey, variables.serviceId), data);
    },
    onError: (error) => {
      console.error('Failed to set service resources:', error);
    }
  });
};

/**
 * Create a service appointment (role-aware).
 * - ADMIN: POST /api/services/:serviceId/appointments with clientId in body
 * - CLIENT: POST /api/client/services/:serviceId/appointments
 *
 * @param serviceId - The service to book
 * @param body - Booking details including startTime and optional primaryResourceId
 */
export const useCreateServiceAppointmentMutation = () => {
  const queryClient = useQueryClient();
  const {
    role,
    assertReady,
    effectiveClientId,
    effectiveCompanyId,
    selectedClientId,
    selectedCompanyId
  } = useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      serviceId,
      body
    }: {
      serviceId: string;
      body: CreateClientServiceAppointmentRequest | CreateServiceAppointmentRequest;
    }): Promise<CreateServiceAppointmentResponse> => {
      assertReady();
      const endpoint = `${roleServicesBase(role)}/${serviceId}/appointments`;
      const bodyToSend = buildAdminMutationBody(role, body, effectiveClientId, effectiveCompanyId);
      return apiPost<CreateServiceAppointmentResponse>(endpoint, bodyToSend);
    },
    onSuccess: () => {
      invalidateAppointmentQueries(queryClient, role, selectedClientId, selectedCompanyId);
    },
    onError: (error) => {
      console.error('Failed to create service appointment:', error);
    }
  });
};

//
// Service Role Mutations
// -------------------------------------------------

/**
 * Create a service role (role-aware).
 * - ADMIN: POST /api/service-roles with clientId in body
 * - CLIENT: POST /api/client/service-roles
 */
export const useCreateServiceRoleMutation = () => {
  const queryClient = useQueryClient();
  const {
    role,
    assertReady,
    invalidateWithRoleKey,
    effectiveClientId,
    effectiveCompanyId
  } = useRoleMutationGate();

  return useMutation({
    mutationFn: async (
      body: CreateClientServiceRoleRequest | CreateServiceRoleRequest
    ): Promise<CreateServiceRoleResponse> => {
      assertReady();
      const bodyToSend = buildAdminMutationBody(role, body, effectiveClientId, effectiveCompanyId);
      return apiPost<CreateServiceRoleResponse>(roleServiceRolesBase(role), bodyToSend);
    },
    onSuccess: () => {
      invalidateWithRoleKey(queryClient, serviceRoleKeys.lists());
    },
    onError: (error) => {
      console.error('Failed to create service role:', error);
    }
  });
};

/**
 * Update a service role by ID (role-aware).
 *
 * @param roleId - The service role to update
 * @param body - Fields to patch
 */
export const useUpdateServiceRoleMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady, invalidateWithRoleKey } = useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      roleId,
      body
    }: {
      roleId: string;
      body: UpdateServiceRoleRequest;
    }): Promise<UpdateServiceRoleResponse> => {
      assertReady();
      const endpoint = `${roleServiceRolesBase(role)}/${roleId}`;
      return apiPut<UpdateServiceRoleResponse>(endpoint, body);
    },
    onSuccess: (data, variables) => {
      invalidateWithRoleKey(queryClient, serviceRoleKeys.lists());
      queryClient.setQueryData(serviceRoleKeys.detail(roleKey, variables.roleId), data);
    },
    onError: (error) => {
      console.error('Failed to update service role:', error);
    }
  });
};

/**
 * Delete a service role by ID (role-aware).
 *
 * @param roleId - The service role to delete
 */
export const useDeleteServiceRoleMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady, invalidateWithRoleKey } = useRoleMutationGate();

  return useMutation({
    mutationFn: async (roleId: string): Promise<void> => {
      assertReady();
      const endpoint = `${roleServiceRolesBase(role)}/${roleId}`;
      await apiDelete<void>(endpoint);
    },
    onSuccess: (_, roleId) => {
      invalidateWithRoleKey(queryClient, serviceRoleKeys.lists());
      queryClient.removeQueries({ queryKey: serviceRoleKeys.detail(roleKey, roleId) });
    },
    onError: (error) => {
      console.error('Failed to delete service role:', error);
    }
  });
};

/**
 * Replace all service roles linked to a resource (role-aware).
 * - ADMIN: PUT /api/resources/:resourceId/service-roles
 * - CLIENT: PUT /api/client/resources/:resourceId/service-roles
 *
 * @param resourceId - The resource whose roles to replace
 * @param body - serviceRoleIds and/or serviceRoleKeys
 */
export const useSetResourceServiceRolesMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady, invalidateWithRoleKey, effectiveClientId } =
    useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      resourceId,
      body
    }: {
      resourceId: string;
      body: SetResourceServiceRolesRequest;
    }): Promise<void> => {
      assertReady();
      const endpoint = `${roleResourcesBase(role)}/${resourceId}/service-roles`;
      const params = withAdminClientParam(role, undefined, effectiveClientId || undefined);
      await apiPut<void>(endpoint, body, { params });
    },
    onSuccess: (_, variables) => {
      invalidateWithRoleKey(queryClient, resourceKeys.lists());
      queryClient.invalidateQueries({ queryKey: resourceKeys.detail(roleKey, variables.resourceId) });
      invalidateWithRoleKey(queryClient, serviceRoleKeys.lists());
    },
    onError: (error) => {
      console.error('Failed to set resource service roles:', error);
    }
  });
};

//
// Appointment Mutations
// -------------------------------------------------

/**
 * Create an appointment (role-aware).
 * - ADMIN: POST /api/appointments with clientId in body
 * - CLIENT: POST /api/client/appointments
 */
export const useCreateAppointmentMutation = () => {
  const queryClient = useQueryClient();
  const {
    role,
    assertReady,
    effectiveClientId,
    effectiveCompanyId,
    selectedClientId,
    selectedCompanyId
  } = useRoleMutationGate();

  return useMutation({
    mutationFn: async (
      body: CreateClientAppointmentRequest | CreateAppointmentRequest
    ): Promise<CreateAppointmentResponse> => {
      assertReady();
      const bodyToSend = buildAdminMutationBody(role, body, effectiveClientId, effectiveCompanyId);
      return apiPost<CreateAppointmentResponse>(roleAppointmentsBase(role), bodyToSend);
    },
    onSuccess: () => {
      invalidateAppointmentQueries(queryClient, role, selectedClientId, selectedCompanyId);
    },
    onError: (error) => {
      console.error('Failed to create appointment:', error);
    }
  });
};

/**
 * Update an appointment — supports status changes and rescheduling (role-aware).
 * - ADMIN: PATCH /api/appointments/:appointmentId
 * - CLIENT: PATCH /api/client/appointments/:appointmentId
 *
 * @param appointmentId - The appointment to update
 * @param body - Fields to patch (status, startTime, endTime)
 */
export const useUpdateAppointmentMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady, selectedClientId, selectedCompanyId } = useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      appointmentId,
      body
    }: {
      appointmentId: string;
      body: UpdateAppointmentRequest;
    }): Promise<UpdateAppointmentResponse> => {
      assertReady();
      const endpoint = `${roleAppointmentsBase(role)}/${appointmentId}`;
      return apiPatch<UpdateAppointmentResponse>(endpoint, body);
    },
    onSuccess: (data, variables) => {
      invalidateAppointmentQueries(queryClient, role, selectedClientId, selectedCompanyId);
      queryClient.setQueryData(appointmentKeys.detail(roleKey, variables.appointmentId), data);
    },
    onError: (error) => {
      console.error('Failed to update appointment:', error);
    }
  });
};

/**
 * Update appointment status only (role-aware).
 * - ADMIN: PATCH /api/appointments/:appointmentId
 * - CLIENT: PATCH /api/client/appointments/:appointmentId
 *
 * @deprecated Use {@link useUpdateAppointmentMutation} instead for better flexibility.
 */
export const useUpdateAppointmentStatusMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady, selectedClientId, selectedCompanyId } = useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      appointmentId,
      body
    }: {
      appointmentId: string;
      body: UpdateAppointmentStatusRequest;
    }): Promise<UpdateAppointmentStatusResponse> => {
      assertReady();
      const endpoint = `${roleAppointmentsBase(role)}/${appointmentId}`;
      return apiPatch<UpdateAppointmentStatusResponse>(endpoint, body);
    },
    onSuccess: (data, variables) => {
      invalidateAppointmentQueries(queryClient, role, selectedClientId, selectedCompanyId);
      queryClient.setQueryData(appointmentKeys.detail(roleKey, variables.appointmentId), data);
    },
    onError: (error) => {
      console.error('Failed to update appointment status:', error);
    }
  });
};

/**
 * Cancel an appointment (role-aware).
 * - ADMIN: PATCH /api/appointments/:appointmentId with { status: 'CANCELLED' }
 * - CLIENT: PATCH /api/client/appointments/:appointmentId with { status: 'CANCELLED' }
 *
 * Respects backend cancellation rules (including cancellation lead time) for all roles.
 *
 * @param appointmentId - The appointment to cancel
 */
export const useCancelAppointmentMutation = () => {
  const queryClient = useQueryClient();
  const { role, roleKey, assertReady, selectedClientId, selectedCompanyId } = useRoleMutationGate();

  return useMutation({
    mutationFn: async (appointmentId: string): Promise<UpdateAppointmentResponse> => {
      assertReady();
      const endpoint = `${roleAppointmentsBase(role)}/${appointmentId}`;
      return apiPatch<UpdateAppointmentResponse>(endpoint, { status: 'CANCELLED' });
    },
    onSuccess: (data, appointmentId) => {
      invalidateAppointmentQueries(queryClient, role, selectedClientId, selectedCompanyId);
      queryClient.setQueryData(appointmentKeys.detail(roleKey, appointmentId), data);
    },
    onError: (error) => {
      console.error('Failed to cancel appointment:', error);
    }
  });
};

/**
 * Delete an appointment by ID (Admin only).
 * - ADMIN: DELETE /api/appointments/:appointmentId
 *
 * This endpoint is only accessible to administrators.
 *
 * @param appointmentId - The appointment to delete
 */
export const useDeleteAppointmentMutation = () => {
  const queryClient = useQueryClient();
  const { role, assertReady, selectedClientId, selectedCompanyId } = useRoleMutationGate();

  return useMutation({
    mutationFn: async (appointmentId: string): Promise<void> => {
      if (!isAdminEquivalentRole(role)) {
        throw new Error('Only admin-equivalent roles can delete appointments');
      }
      assertReady();
      const endpoint = `${roleAppointmentsBase(role)}/${appointmentId}`;
      await apiDelete<void>(endpoint);
    },
    onSuccess: (_, appointmentId) => {
      invalidateAppointmentQueries(queryClient, role, selectedClientId, selectedCompanyId);
      queryClient.removeQueries({ queryKey: appointmentKeys.adminDetail(appointmentId) });
    },
    onError: (error) => {
      console.error('Failed to delete appointment:', error);
    }
  });
};

/**
 * Create a reschedule request for one of the authenticated customer's appointments.
 * POST /mcp-appointments/api/customer/appointments/:appointmentId/reschedule-requests
 */
export const useCreateCustomerRescheduleRequestMutation = () => {
  const queryClient = useQueryClient();
  const { role, assertReady, selectedClientId, selectedCompanyId } = useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      appointmentId,
      body
    }: {
      appointmentId: string;
      body: CreateCustomerRescheduleRequestBody;
    }): Promise<AppointmentRescheduleRequest> => {
      assertReady();
      if (role !== 'CUSTOMER') {
        throw new Error('Only customers can create reschedule requests via this endpoint');
      }
      const endpoint = `${roleAppointmentsBase(role)}/${appointmentId}/reschedule-requests`;
      return apiPost<AppointmentRescheduleRequest>(endpoint, body);
    },
    onSuccess: () => {
      invalidateAppointmentQueries(queryClient, role, selectedClientId, selectedCompanyId);
      void queryClient.invalidateQueries({
        queryKey: appointmentKeys.rescheduleRequestsAll()
      });
    },
    onError: (error) => {
      console.error('Failed to create reschedule request:', error);
    }
  });
};

export const useCreateCustomerCancellationRequestMutation = () => {
  const queryClient = useQueryClient();
  const { role, assertReady, selectedClientId, selectedCompanyId } = useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      appointmentId,
      body
    }: {
      appointmentId: string;
      body: CreateCustomerCancellationRequestBody;
    }): Promise<AppointmentCancellationRequest> => {
      assertReady();
      if (role !== 'CUSTOMER') {
        throw new Error('Only customers can create cancellation requests via this endpoint');
      }
      const endpoint = `${roleAppointmentsBase(role)}/${appointmentId}/cancellation-requests`;
      return apiPost<AppointmentCancellationRequest>(endpoint, body);
    },
    onSuccess: () => {
      invalidateAppointmentQueries(queryClient, role, selectedClientId, selectedCompanyId);
    },
    onError: (error) => {
      console.error('Failed to create cancellation request:', error);
    }
  });
};

export const useApproveClientRescheduleRequestMutation = () => {
  const queryClient = useQueryClient();
  const { role, assertReady, selectedClientId, selectedCompanyId, effectiveClientId, effectiveCompanyId } =
    useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      requestId,
      body
    }: {
      requestId: string;
      body?: ResolveChangeRequestBody;
    }): Promise<ApproveRescheduleRequestResponse> => {
      assertReady();
      const scopeParams = withAdminScopeParams(
        role,
        {},
        { clientId: effectiveClientId ?? selectedClientId, companyId: effectiveCompanyId ?? selectedCompanyId }
      );
      return apiPost<ApproveRescheduleRequestResponse>(
        `${roleRescheduleRequestsBase(role)}/${requestId}/approve`,
        body ?? {},
        canUseAdminTenantScope(role) ? { params: scopeParams ?? undefined } : undefined
      );
    },
    onSuccess: () => {
      invalidateAppointmentQueries(queryClient, role, selectedClientId, selectedCompanyId);
      void queryClient.invalidateQueries({ queryKey: appointmentKeys.rescheduleRequestsAll() });
    }
  });
};

export const useRejectClientRescheduleRequestMutation = () => {
  const queryClient = useQueryClient();
  const { role, assertReady, selectedClientId, selectedCompanyId, effectiveClientId, effectiveCompanyId } =
    useRoleMutationGate();

  return useMutation({
    mutationFn: async ({
      requestId,
      body
    }: {
      requestId: string;
      body?: ResolveChangeRequestBody;
    }): Promise<AppointmentRescheduleRequest> => {
      assertReady();
      const scopeParams = withAdminScopeParams(
        role,
        {},
        { clientId: effectiveClientId ?? selectedClientId, companyId: effectiveCompanyId ?? selectedCompanyId }
      );
      return apiPost<AppointmentRescheduleRequest>(
        `${roleRescheduleRequestsBase(role)}/${requestId}/reject`,
        body ?? {},
        canUseAdminTenantScope(role) ? { params: scopeParams ?? undefined } : undefined
      );
    },
    onSuccess: () => {
      invalidateAppointmentQueries(queryClient, role, selectedClientId, selectedCompanyId);
      void queryClient.invalidateQueries({ queryKey: appointmentKeys.rescheduleRequestsAll() });
    }
  });
};
